import { useState, useLayoutEffect, RefObject } from "react";

interface DropdownPositionOptions {
  offset?: number; // distance below the button
  extraWidth?: number; // added to trigger width (used when centered)
  centered?: boolean; // center below trigger
  alignRight?: boolean; // align dropdown's right edge with trigger's right edge
  customWidth?: number; // force a custom width instead of based on trigger width
  margin?: number;
  fullWidth?: boolean;
}

export function useDropdownPosition(
  triggerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  options: DropdownPositionOptions = {}
) {
  const {
    offset = 12,
    extraWidth = 20,
    centered = true,
    alignRight = false,
    customWidth,
    margin = 16,
    fullWidth = false,
  } = options;

  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) return;

    function updatePosition() {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const dir = document?.dir || "ltr";
      const isRTL = dir === "rtl";

      const viewportWidth = window.innerWidth;
      const maxWidth = viewportWidth - 2 * margin;

      // 1️⃣ Width calculation
      let width = fullWidth
        ? rect.width
        : customWidth ?? rect.width + extraWidth;

      if (width > maxWidth) width = maxWidth;

      // 2️⃣ Compute base left position (in physical coordinates)
      let left: number;
      if (centered) {
        left = rect.left + rect.width / 2 - width / 2;
      } else if (alignRight) {
        left = rect.right - width;
      } else {
        left = rect.left;
      }

      // 3️⃣ RTL translation (visual mirroring)
      // In RTL, "alignRight" visually means attach to the LEFT edge,
      // and "left" visually means attach to the RIGHT edge.
      if (isRTL) {
        const mirroredLeft = viewportWidth - (left + width);
        left = mirroredLeft;
      }

      // 4️⃣ Clamp safely within viewport bounds
      if (left < margin) left = margin;
      if (left + width > viewportWidth - margin)
        left = viewportWidth - width - margin;

      // 5️⃣ Top position (always the same)
      const top = rect.bottom + offset;

      const style: React.CSSProperties = {
        position: "fixed",
        top: `${top}px`,
        width: `${width}px`,
        zIndex: 1000,
        visibility: "visible",
      };

      // In RTL, position by `right` instead of `left`
      if (isRTL) {
        style.right = `${left}px`; // "left" here is now the mirrored offset
      } else {
        style.left = `${left}px`;
      }

      setStyle(style);
    }

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [
    isOpen,
    triggerRef,
    offset,
    extraWidth,
    centered,
    alignRight,
    customWidth,
  ]);

  return style;
}
